import { API, graphqlOperation } from "aws-amplify";
import { listInterviewEvaluations } from "../graphql/queries";

/**
 * Utility function to fetch interview evaluations by candidate email and optionally by interview slot ID
 * @param {string} candidateEmail - The email of the candidate
 * @param {string|null} interviewSlotId - Optional interview slot ID to filter by
 * @param {number} limit - Number of items to fetch per request, default 100
 * @returns {Array} - Array of interview evaluations
 */
export const fetchInterviewEvaluations = async (candidateEmail, interviewSlotId = null, limit = 100) => {
  try {
    let nextToken = null;
    let evaluations = [];
    
    do {
      // Prepare filter based on available parameters
      let filter = {
        candidateEmail: { eq: candidateEmail }
      };
      
      // Add interviewSlotID filter if it's provided
      if (interviewSlotId) {
        filter.interviewSlotID = { eq: interviewSlotId };
      }
      
      const response = await API.graphql(
        graphqlOperation(listInterviewEvaluations, {
          filter: filter,
          limit: limit,
          nextToken: nextToken,
        })
      );
      
      const items = response.data.listInterviewEvaluations.items;
      evaluations = [...evaluations, ...items];
      nextToken = response.data.listInterviewEvaluations.nextToken;
    } while (nextToken);
    
    return evaluations;
  } catch (error) {
    console.error("Error fetching interview evaluations:", error);
    throw error;
  }
};

/**
 * Utility function to group evaluations by interview slot
 * @param {Array} evaluations - Array of interview evaluations
 * @returns {Object} - Object with interview slot IDs as keys and arrays of evaluations as values
 */
export const groupEvaluationsByInterviewSlot = (evaluations) => {
  return evaluations.reduce((groups, evaluation) => {
    const slotId = evaluation.interviewSlotID || 'unknown';
    if (!groups[slotId]) {
      groups[slotId] = [];
    }
    groups[slotId].push(evaluation);
    return groups;
  }, {});
};

/**
 * Get the latest evaluation for each interview slot
 * @param {Array} evaluations - Array of interview evaluations
 * @returns {Array} - Array of the latest evaluation for each slot
 */
export const getLatestEvaluationsBySlot = (evaluations) => {
  const groupedBySlot = groupEvaluationsByInterviewSlot(evaluations);
  
  return Object.values(groupedBySlot).map(slotEvaluations => {
    // Sort by createdAt in descending order and take the first one
    return slotEvaluations.sort((a, b) => 
      new Date(b.createdAt) - new Date(a.createdAt)
    )[0];
  });
};

/**
 * Enhanced fetch function that prioritizes slot ID matching
 * @param {string} candidateEmail - The email of the candidate
 * @param {string|null} preferredSlotId - Preferred interview slot ID to filter by
 * @param {number} limit - Number of items to fetch per request, default 100
 * @returns {Object} - Object containing evaluations array and metadata
 */
export const fetchEvaluationsWithFallback = async (candidateEmail, preferredSlotId = null, limit = 100) => {
  try {
    console.log(`🔍 Fetching evaluations for ${candidateEmail} with slot: ${preferredSlotId}`);
    
    if (!preferredSlotId) {
      console.log(`⚠️  No slot ID provided, will implement fallback logic later`);
      // For now, return empty if no slot ID - as requested
      return {
        evaluations: [],
        exactMatch: false,
        totalCount: 0,
        uniqueSlotIds: [],
        preferredSlotId: null
      };
    }
    
    // Priority: Get evaluations for the specific slot ID + email combination
    const evaluations = await fetchInterviewEvaluations(candidateEmail, preferredSlotId, limit);
    
    if (evaluations.length > 0) {
      console.log(`✅ Found ${evaluations.length} evaluations for exact slot match`);
      return {
        evaluations,
        exactMatch: true,
        totalCount: evaluations.length,
        uniqueSlotIds: [...new Set(evaluations.map(e => e.interviewSlotID))],
        preferredSlotId
      };
    } else {
      console.log(`❌ No evaluations found for slot ${preferredSlotId}`);
      // Return empty results for now - as requested, we'll handle email-only case later
      return {
        evaluations: [],
        exactMatch: false,
        totalCount: 0,
        uniqueSlotIds: [],
        preferredSlotId
      };
    }
    
  } catch (error) {
    console.error("Error in fetchEvaluationsWithFallback:", error);
    throw error;
  }
};

/**
 * Debug utility to find all available slot IDs for a candidate
 * This helps identify slot ID mismatches
 * @param {string} candidateEmail - The email of the candidate
 * @returns {Array} - Array of available slot IDs for the candidate
 */
export const debugAvailableSlots = async (candidateEmail) => {
  try {
    console.log(`🔧 DEBUG: Finding all available slots for ${candidateEmail}`);
    
    // Get all evaluations for the candidate (no slot filter)
    const allEvaluations = await fetchInterviewEvaluations(candidateEmail, null, 100);
    
    if (allEvaluations.length === 0) {
      console.log(`❌ No evaluations found for ${candidateEmail}`);
      return [];
    }
    
    const uniqueSlotIds = [...new Set(allEvaluations.map(e => e.interviewSlotID))];
    console.log(`🎰 Found ${allEvaluations.length} evaluations across ${uniqueSlotIds.length} slots:`);
    
    uniqueSlotIds.forEach((slotId, index) => {
      const slotEvaluations = allEvaluations.filter(e => e.interviewSlotID === slotId);
      console.log(`   ${index + 1}. Slot ID: ${slotId} (${slotEvaluations.length} evaluations)`);
    });
    
    return uniqueSlotIds;
    
  } catch (error) {
    console.error("Error in debugAvailableSlots:", error);
    return [];
  }
};
